import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImpersonation } from '@shared/contexts/ImpersonationContext';
import { useDialog } from '@shared/hooks/useDialog';
import { getToken } from '@shared/services/api';

/**
 * Feature 712: Keyboard shortcut to exit impersonation
 *
 * Pressing ESC key while impersonating shows confirmation dialog to exit impersonation mode.
 *
 * This component listens for the ESC key globally and shows a confirmation modal
 * when the user is in an active impersonation session.
 */
export default function ExitImpersonationModal() {
  const { t } = useTranslation('common');
  const { isImpersonating, impersonatedUserInfo, clearImpersonationState } = useImpersonation();
  const { showError } = useDialog();
  const [showModal, setShowModal] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Listen for ESC key to show confirmation modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Feature 712: Only trigger ESC if impersonating
      if (e.key === 'Escape' && isImpersonating && !showModal) {
        e.preventDefault();
        setShowModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isImpersonating, showModal]);

  // Handle exit impersonation
  const handleExitImpersonation = async () => {
    setIsExiting(true);

    try {
      const token = getToken();

      // Call backend endpoint to stop impersonation
      const response = await fetch('/api/v1/admin/stop-impersonating', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();

        // Restore admin token to localStorage
        localStorage.setItem('auth_token', data.token);

        // Clear impersonation state using context
        clearImpersonationState();

        // Redirect to admin dashboard
        window.location.href = '/admin/customers';
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to stop impersonation:', await response.json());
        await showError(t('status.error'), t('impersonation.stopFailed'));
        setShowModal(false);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to stop impersonation:', err);
      await showError(t('status.error'), t('impersonation.stopConnectionFailed'));
      setShowModal(false);
    } finally {
      setIsExiting(false);
    }
  };

  // Handle ESC key to dismiss modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal) {
        e.preventDefault();
        setShowModal(false);
      }
    };

    if (showModal) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  // Don't render anything if not impersonating
  if (!isImpersonating) {
    return null;
  }

  return (
    <>
      {/* Feature 712: Confirmation Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="exit-impersonation-title"
          aria-describedby="exit-impersonation-description"
        >
          <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-state-warn-bg-light dark:bg-state-warn-bg-dark rounded-full flex items-center justify-center">
                <span className="text-xl" role="img" aria-label="warning">
                  ⚠️
                </span>
              </div>
              <h2
                id="exit-impersonation-title"
                className="text-xl font-semibold text-gray-900 dark:text-white"
              >
                {t('impersonation.exitConfirmTitle')}
              </h2>
            </div>

            {/* Description */}
            <div className="mb-6">
              <p id="exit-impersonation-description" className="text-slate-600 dark:text-slate-300">
                {t('impersonation.exitConfirmMessage', {
                  user:
                    impersonatedUserInfo?.name ||
                    impersonatedUserInfo?.email ||
                    t('impersonation.unknownUser'),
                })}
              </p>
              <p className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark">
                {t('impersonation.loggedActionsNote')}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              {/* Cancel Button */}
              <button
                onClick={() => setShowModal(false)}
                disabled={isExiting}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-md hover:bg-surface-2-light dark:hover:bg-surface-2-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('actions.cancel')}
              </button>

              {/* Confirm Exit Button */}
              <button
                onClick={handleExitImpersonation}
                disabled={isExiting}
                className="px-4 py-2 text-sm font-medium text-white bg-state-warn-light border border-transparent rounded-md hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-state-warn-light disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 dark:text-on-accent-dark"
              >
                {isExiting ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>{t('impersonation.exitInProgress')}</span>
                  </>
                ) : (
                  <span>{t('impersonation.exitImpersonation')}</span>
                )}
              </button>
            </div>

            {/* Keyboard shortcut hint */}
            <div className="mt-4 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
              <p className="text-xs text-center text-text-muted-light dark:text-text-muted-dark">
                {t('impersonation.dismissHint')}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
